// 상품 상세 화면.
// 작업계획서 4.4 (17, 18): 메인 사진, 이름, 가격(단위), 원산지, 상세 정보 사진들, 찜 버튼, 장바구니 담기 버튼.
// 본 라운드(Phase 2)에서는 장바구니 담기는 placeholder 안내, 찜 토글만 동작.
// 손님 카피 톤([[feedback-customer-copy-tone]]) 적용.

import { useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Minus, Plus } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CART_ITEM_QUANTITY_MAX, resolveUnitLabel } from "@oo/shared";
import type { CartItemOptionInput } from "@oo/shared";
import ProductOptionsModal from "@/features/catalog/ProductOptionsModal.tsx";
import {
  addToWishlist,
  checkWishlist,
  getProduct,
  removeFromWishlist,
} from "@/features/catalog/catalogApi.ts";
import { imageUrl } from "@/features/catalog/imageUrl.ts";
import { useUnits } from "@/features/catalog/useUnits.ts";
import { useAuthStore } from "@/stores/authStore.ts";
import { ApiError } from "@/api/client.ts";
import { useCart } from "@/features/cart/useCart.ts";
import { Button } from "@/shared/ui";

// 상품 상세 화면 (담기/찜).
export default function ProductDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const session = useAuthStore((s) => s.session);
  const units = useUnits();

  const product = useQuery({
    queryKey: ["catalog", "product", id],
    queryFn: () => getProduct(id),
    enabled: !!id,
  });

  // 찜 여부: 로그인 사용자만 조회.
  const wished = useQuery({
    queryKey: ["catalog", "wishlist-check", id, session?.user.id ?? null],
    queryFn: async () => {
      const map = await checkWishlist([id]);
      return map[id] ?? false;
    },
    enabled: !!id && !!session,
  });

  // 찜 토글: 낙관적 업데이트(하트가 즉시 반응, 실패 시 롤백). 버벅임 제거.
  const wishKey = ["catalog", "wishlist-check", id, session?.user.id ?? null];
  const wishToggle = useMutation<void, unknown, boolean, { prev: boolean | undefined }>({
    mutationFn: async (next: boolean) => {
      if (next) {
        await addToWishlist(id);
      } else {
        await removeFromWishlist(id);
      }
    },
    onMutate: async (next: boolean) => {
      await qc.cancelQueries({ queryKey: wishKey });
      const prev = qc.getQueryData<boolean>(wishKey);
      qc.setQueryData(wishKey, next); // 즉시 반영
      return { prev };
    },
    onError: (err: unknown, _next, ctx) => {
      qc.setQueryData(wishKey, ctx?.prev); // 롤백
      if (err instanceof ApiError && err.status === 401) {
        navigate("/login", { state: { from: `/product/${id}` } });
      } else {
        // 인앱 토스트로 안내(네이티브 alert 대신 - 일관된 UX/접근성)
        showToast(err instanceof Error ? err.message : "잠시 후 다시 시도해 주세요.");
      }
    },
    // 서버와 최종 동기화(낙관적 값과 일치하면 변화 없음).
    onSettled: () => qc.invalidateQueries({ queryKey: ["catalog", "wishlist-check", id] }),
  });

  function onClickWish() {
    if (!session) {
      navigate("/login", { state: { from: `/product/${id}` } });
      return;
    }
    wishToggle.mutate(!wished.data);
  }

  // 상세 화면에서는 수량을 선택해 한 번에 담는다.
  const cart = useCart();
  const [qty, setQty] = useState(1);
  const [adding, setAdding] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  // 추가 옵션이 있는 상품의 담기 모달 열림 여부.
  const [optionsOpen, setOptionsOpen] = useState(false);

  function showToast(msg: string) {
    setToast(msg);
    window.setTimeout(() => setToast(null), 1800);
  }

  // 담기 실행(옵션 유무 공용). options 는 모달에서 고른 추가 옵션.
  async function addToCart(quantity: number, options?: CartItemOptionInput[]) {
    if (adding || !product.data || product.data.isSoldOut) return;
    setAdding(true);
    try {
      const result = await cart.addItem(product.data.id, quantity, product.data, options);
      if (result.ok) {
        showToast(result.capped ? "최대 수량까지 담았습니다." : "장바구니에 담았습니다.");
        setOptionsOpen(false);
      } else if (result.reason === "distinct-limit") {
        showToast("장바구니 종류 한도를 초과했습니다.");
      } else if (result.reason === "sold-out") {
        showToast("이 상품은 지금 담을 수 없습니다.");
      } else {
        showToast("상품을 찾을 수 없습니다.");
      }
    } finally {
      setAdding(false);
    }
  }

  // 하단 담기 버튼: 추가 옵션이 있으면 선택 모달을 먼저 띄운다.
  function onClickAddToCart() {
    if (!product.data) return;
    if (product.data.options.length > 0) {
      setOptionsOpen(true);
      return;
    }
    void addToCart(qty);
  }

  if (product.isLoading) {
    return (
      <div className="flex flex-col">
        <header className="sticky top-[var(--safe-top)] z-10 flex items-center border-b border-ink-200 bg-white px-4 py-3">
          <Link to="/" className="rounded-md px-2 py-1 text-sm hover:bg-ink-100">← 뒤로</Link>
        </header>
        <main className="mx-auto w-full max-w-screen-sm px-4 py-4">
          <div className="aspect-square w-full animate-pulse rounded-lg bg-ink-100" />
        </main>
      </div>
    );
  }
  if (product.isError || !product.data) {
    return (
      <div className="flex flex-col">
        <header className="sticky top-[var(--safe-top)] z-10 flex items-center border-b border-ink-200 bg-white px-4 py-3">
          <Link to="/" className="rounded-md px-2 py-1 text-sm hover:bg-ink-100">← 뒤로</Link>
        </header>
        <main className="mx-auto w-full max-w-screen-sm p-8 text-center">
          <p className="text-sm text-ink-600">상품을 찾을 수 없습니다.</p>
        </main>
      </div>
    );
  }

  const p = product.data;
  const mainUrl = imageUrl(p.mainImageKey);
  const unitLabel = resolveUnitLabel(units, p.unit, p.packWeightG);

  return (
    <div className="flex flex-col pb-[calc(6rem+var(--safe-bottom))]">
      <header className="sticky top-[var(--safe-top)] z-10 flex items-center gap-2 border-b border-ink-200 bg-white px-4 py-3">
        <button type="button" onClick={() => navigate(-1)} className="rounded-md px-2 py-1 text-sm hover:bg-ink-100" aria-label="뒤로">
          ← 뒤로
        </button>
      </header>
      <main className="mx-auto w-full max-w-screen-sm">
        {/* 메인 이미지 */}
        <div className="relative aspect-square w-full bg-ink-100">
          {mainUrl ? (
            <img
              src={mainUrl}
              alt={p.name}
              // 상세 메인 이미지는 LCP 후보라 즉시 로드 + 디코딩 비동기.
              fetchPriority="high"
              decoding="async"
              className={`h-full w-full object-cover ${p.isSoldOut ? "opacity-50 grayscale" : ""}`}
            />
          ) : (
            <div className="flex h-full w-full items-center justify-center text-sm text-ink-600">
              이미지 준비중
            </div>
          )}
          {p.isSoldOut ? (
            <span className="absolute left-3 top-3 rounded bg-ink-900/80 px-3 py-1 text-sm font-semibold text-white">
              품절
            </span>
          ) : null}
        </div>

        <section className="flex flex-col gap-3 p-4">
          <h1 className={`text-xl font-bold ${p.isSoldOut ? "text-ink-400" : ""}`}>{p.name}</h1>
          {p.origin ? <p className="text-sm text-ink-600">원산지: {p.origin}</p> : null}
          <div className="flex items-baseline gap-1">
            <span className={`text-2xl font-bold tabular-nums ${p.isSoldOut ? "text-ink-400" : ""}`}>
              {p.priceKrw.toLocaleString()}원
            </span>
            {unitLabel ? <span className="text-sm text-ink-600">/ {unitLabel}</span> : null}
          </div>
        </section>

        {p.description ? (
          <section className="border-t border-ink-200 p-4">
            <h2 className="mb-2 text-sm font-bold text-ink-700">상품 설명</h2>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-800">{p.description}</p>
          </section>
        ) : null}

        {p.detailImageKeys.length > 0 ? (
          <section className="border-t border-ink-200 p-4">
            <h2 className="mb-3 text-sm font-bold text-ink-700">상세 정보</h2>
            <div className="flex flex-col gap-3">
              {p.detailImageKeys.map((k, i) => {
                const u = imageUrl(k);
                return u ? (
                  <img key={`${k}-${i}`} src={u} alt="" loading="lazy" decoding="async" className="w-full rounded-md" />
                ) : null;
              })}
            </div>
          </section>
        ) : null}
      </main>

      {/* 하단 고정 액션 바: 찜 + 수량 선택 + 장바구니 (작업계획서 17, 18) */}
      <div className="fixed inset-x-0 bottom-[calc(4rem+var(--safe-bottom))] z-10 border-t border-ink-200 bg-white px-4 py-3">
        <div className="mx-auto flex max-w-screen-sm flex-col gap-2">
          {/* 수량 선택 */}
          {!p.isSoldOut ? (
            <div className="flex items-center justify-between">
              <span className="text-sm text-ink-600">수량</span>
              <div className="flex items-center rounded-md border border-ink-300">
                <button
                  type="button"
                  onClick={() => setQty((v) => Math.max(1, v - 1))}
                  className="inline-flex min-h-touch min-w-touch items-center justify-center text-ink-700 disabled:opacity-40"
                  aria-label="수량 감소"
                  disabled={qty <= 1}
                >
                  <Minus size={18} aria-hidden="true" />
                </button>
                <span className="min-w-[2.5rem] text-center text-base tabular-nums">{qty}</span>
                <button
                  type="button"
                  onClick={() => setQty((v) => Math.min(CART_ITEM_QUANTITY_MAX, v + 1))}
                  className="inline-flex min-h-touch min-w-touch items-center justify-center text-ink-700 disabled:opacity-40"
                  aria-label="수량 증가"
                  disabled={qty >= CART_ITEM_QUANTITY_MAX}
                >
                  <Plus size={18} aria-hidden="true" />
                </button>
              </div>
            </div>
          ) : null}

          <div className="flex gap-2">
            <button
              type="button"
              onClick={onClickWish}
              disabled={wishToggle.isPending}
              className="flex h-12 w-14 flex-col items-center justify-center rounded-md border border-ink-300 text-xs hover:bg-ink-50 disabled:opacity-50"
              aria-pressed={wished.data ?? false}
              aria-label={wished.data ? "찜 해제" : "찜 추가"}
            >
              {wished.data ? "찜됨" : "찜"}
            </button>
            <Button
              type="button"
              onClick={onClickAddToCart}
              disabled={p.isSoldOut || adding}
              loading={adding}
              className="flex-1"
            >
              {p.isSoldOut ? "품절" : adding ? "담는 중..." : "장바구니 담기"}
            </Button>
          </div>
        </div>
        {toast ? (
          <div className="pointer-events-none mx-auto mt-2 max-w-screen-sm">
            <p role="status" className="rounded-md bg-ink-900/90 px-3 py-2 text-center text-sm text-white">{toast}</p>
          </div>
        ) : null}
      </div>

      {/* 추가 옵션 선택 모달(옵션이 있는 상품만). */}
      {optionsOpen ? (
        <ProductOptionsModal
          product={p}
          busy={adding}
          onClose={() => setOptionsOpen(false)}
          onConfirm={(quantity, options) => addToCart(quantity, options)}
        />
      ) : null}
    </div>
  );
}
